import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../services/api';
import ItemList from './ItemList';
import NotesTab from './NotesTab';
import GroceriesTab from './GroceriesTab';
import './CategoryTabs.css';

const CategoryTabs = ({ houseId, initialTab, onTabChange }) => {
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab || 'groceries');
  const [loading, setLoading] = useState(true);
  
  // Update activeTab when initialTab prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (houseId) {
      loadCategories();
    }
  }, [houseId]);

  const loadCategories = async () => {
    if (!houseId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoriesAPI.getAll(houseId);
      setCategories(response.data);
      
      // Only set default tab if initialTab is not set (preserve navigation intent)
      if (!initialTab) {
        // Set default tab to 'groceries' (first tab)
        setActiveTab('groceries');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryId = (tabName) => {
    if (tabName === 'clothes') {
      const clothesCat = categories.find(cat => cat.name.toLowerCase() === 'clothes');
      return clothesCat ? clothesCat.id : null;
    }
    if (tabName === 'medicines') {
      const medCat = categories.find(cat => cat.name.toLowerCase() === 'medicines' || cat.name.toLowerCase() === 'medications');
      return medCat ? medCat.id : null;
    }
    if (tabName === 'notes') {
      return null;
    }
    return parseInt(tabName);
  };

  if (!houseId) {
    return <div className="category-tabs-message">Please select a house</div>;
  }

  if (loading) {
    return <div className="category-tabs-loading">Loading categories...</div>;
  }

  return (
    <div className="category-tabs">
      <div className="tabs-header">
        <button
          className={`tab-button ${activeTab === 'groceries' ? 'active' : ''}`}
          onClick={() => setActiveTab('groceries')}
        >
          🛒 Groceries
        </button>
        <button
          className={`tab-button ${activeTab === 'clothes' ? 'active' : ''}`}
          onClick={() => setActiveTab('clothes')}
        >
          Clothes
        </button>
        <button
          className={`tab-button ${activeTab === 'medicines' ? 'active' : ''}`}
          onClick={() => setActiveTab('medicines')}
        >
          Medicines
        </button>
        {categories
          .filter(cat => {
            const name = cat.name.toLowerCase();
            return name !== 'clothes' && name !== 'medicines' && name !== 'medications';
          })
          .map((category) => (
            <button
              key={category.id}
              className={`tab-button ${activeTab === category.id.toString() ? 'active' : ''}`}
              onClick={() => setActiveTab(category.id.toString())}
            >
              {category.name}
            </button>
          ))}
        <button
          className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
      </div>

      <div className="tabs-content">
        {activeTab === 'groceries' ? (
          <GroceriesTab houseId={houseId} />
        ) : activeTab === 'notes' ? (
          <NotesTab houseId={houseId} />
        ) : categories.length === 0 ? (
          <div className="category-tabs-empty">
            <p>No categories found for this house.</p>
            <p>Categories will be created automatically when you add your first item, or you can create them via the API.</p>
            <p>Default categories (Clothes, Medicines) are created when you create a new house.</p>
          </div>
        ) : getCategoryId(activeTab) ? (
          <ItemList houseId={houseId} categoryId={getCategoryId(activeTab)} />
        ) : (
          <div className="category-tabs-empty">
            <p>Category not found. Please select a different tab.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTabs;

